// Cookie Manager - Handles blocking/unblocking of external scripts based on consent
// Based on manual implementation approach (vanilla JavaScript)

/**
 * Enable scripts for a specific category
 * Finds all blocked scripts with data-cookiecategory attribute and activates them
 * @param {string} category - Cookie category (necessary, analytics, marketing)
 */
export const enableScripts = (category) => {
  // Find all scripts with the specified category
  const scripts = document.querySelectorAll(`script[data-cookiecategory="${category}"]`);

  scripts.forEach((script) => {
    // Create new script element to trigger execution
    const newScript = document.createElement('script');

    // Copy attributes
    if (script.src) {
      newScript.src = script.src;
      newScript.async = script.async || false;
    } else {
      newScript.innerHTML = script.innerHTML;
    }

    // Set proper type to execute
    newScript.type = 'text/javascript';

    // Copy other attributes
    Array.from(script.attributes).forEach((attr) => {
      if (attr.name !== 'type' && attr.name !== 'data-cookiecategory') {
        newScript.setAttribute(attr.name, attr.value);
      }
    });

    // Replace old blocked script with new active one
    script.parentNode.replaceChild(newScript, script);
  });

  console.log(`Scripts enabled for category: ${category}`);
};

/**
 * Disable scripts for a specific category
 * Marks scripts as blocked by changing type to text/plain
 * @param {string} category - Cookie category
 */
export const disableScripts = (category) => {
  const scripts = document.querySelectorAll(`script[data-cookiecategory="${category}"]`);

  scripts.forEach((script) => {
    script.type = 'text/plain';
  });

  console.log(`Scripts disabled for category: ${category}`);
};

/**
 * Load Meta Pixel (Facebook Pixel) script dynamically
 * Only called when marketing consent is given
 */
export const loadMetaPixel = () => {
  if (window.fbq) {
    // Already loaded
    return;
  }

  // Meta Pixel base code
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js',
  );
  /* eslint-enable */

  // Initialize Meta Pixel with your Pixel ID
  if (window.fbq && typeof window.fbq === 'function') {
    window.fbq('init', '1578808199930976');
    window.fbq('track', 'PageView');
  }

  console.log('Meta Pixel loaded');
};

/**
 * Remove Meta Pixel script
 * Called when marketing consent is revoked
 * Note: Safer to reload page after consent change to avoid React crashes
 */
export const unloadMetaPixel = () => {
  if (!window.fbq) {
    return;
  }

  // Remove script tags to stop new requests
  const scripts = document.querySelectorAll(
    'script[src*="facebook.net/en_US/fbevents.js"]',
  );
  scripts.forEach((script) => script.remove());

  // Remove first-party Facebook cookies (third-party cookies can't be deleted by JS)
  const fbCookies = ['_fbp', 'fr', '_fbc'];
  const hostname = window.location.hostname;
  const domain = hostname.startsWith('www.')
    ? hostname.substring(4)
    : hostname;

  fbCookies.forEach((cookieName) => {
    // Try multiple domain variations to ensure deletion
    const variations = [
      `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`,
      `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname};`,
      `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`,
      `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`,
    ];

    variations.forEach((cookieString) => {
      document.cookie = cookieString;
    });
  });

  // Note: We don't delete window.fbq here to avoid breaking React components
  // The script won't load again until user accepts marketing cookies
  console.log('Meta Pixel scripts removed. Page reload recommended for full cleanup.');
};

/**
 * Apply consent settings
 * Enable/disable scripts based on current consent
 * @param {Object} consent - Consent object with necessary, analytics, marketing flags
 */
export const applyConsent = (consent) => {
  // Necessary scripts - always enabled
  enableScripts('necessary');

  // Analytics scripts (Google Analytics, Hotjar)
  if (consent.analytics) {
    enableScripts('analytics');
  } else {
    disableScripts('analytics');
  }

  // Marketing scripts (Meta Pixel, Google Ads, YouTube)
  if (consent.marketing) {
    enableScripts('marketing');
    loadMetaPixel(); // Also load dynamic Meta Pixel
  } else {
    disableScripts('marketing');
    unloadMetaPixel();
  }

};

/**
 * Listen for consent changes and apply them
 */
export const initCookieManager = () => {
  // Clean up legacy i18next cookie (now we use only localStorage)
  const hostname = window.location.hostname;
  const domain = hostname.startsWith('www.')
    ? hostname.substring(4)
    : hostname;

  const i18nextCookieVariations = [
    'i18nextLng=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;',
    `i18nextLng=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname};`,
    `i18nextLng=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`,
    `i18nextLng=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`,
  ];

  i18nextCookieVariations.forEach((cookieString) => {
    document.cookie = cookieString;
  });

  window.addEventListener('cookieConsentChange', (event) => {
    const consent = event.detail;
    applyConsent(consent);
  });
};
